import SectionHeading from "../components/SectionHeading";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ui, useLang } from "../i18n/i18n";

export default function About() {
  const { t } = useLang();
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="border-b border-[#E5E7EB]" data-testid="about-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-8">
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                {t(ui.about.crumb)}
              </div>
              <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
              <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0B1F3A] leading-[1.04]">
                {t(ui.about.h1)}
              </h1>
              <p className="mt-8 text-[#4B5563] text-[17px] md:text-[19px] max-w-2xl leading-relaxed">
                {t(ui.about.lead)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="border-b border-[#E5E7EB] bg-[#F7F8FA]" data-testid="mission-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-5">
              <SectionHeading idx="02" eyebrow={ui.about.missionEyebrow} title={ui.about.missionTitle} />
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7">
              <p className="text-[#4B5563] text-[17px] leading-relaxed">{t(ui.about.missionP1)}</p>
              <p className="mt-6 text-[#4B5563] text-[17px] leading-relaxed">{t(ui.about.missionP2)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="border-b border-[#E5E7EB]" data-testid="experience-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-5">
              <SectionHeading idx="03" eyebrow={ui.about.experienceEyebrow} title={ui.about.experienceTitle} />
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7">
              <p className="text-[#4B5563] text-[17px] leading-relaxed">{t(ui.about.experienceP1)}</p>
              <p className="mt-6 text-[#4B5563] text-[17px] leading-relaxed">{t(ui.about.experienceP2)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-b border-[#E5E7EB]" data-testid="values-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <SectionHeading idx="04" eyebrow={ui.about.valuesEyebrow} title={ui.about.valuesTitle} />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {ui.values.map((v) => (
              <div key={v.idx} data-testid={`value-${v.idx}`} className="bg-white p-8 md:p-10">
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {v.idx}
                </div>
                <div className="mt-4 text-xl font-semibold text-[#0B1F3A] tracking-tight">
                  {t(v.title)}
                </div>
                <p className="mt-4 text-[#4B5563] text-sm leading-relaxed">{t(v.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-b border-[#E5E7EB] bg-[#F7F8FA]" data-testid="process-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <SectionHeading
            idx="05"
            eyebrow={ui.about.processEyebrow}
            title={ui.about.processTitle}
            description={ui.about.processDesc}
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {ui.process.map((step) => (
              <div key={step.idx} data-testid={`process-${step.idx}`} className="bg-white p-8 md:p-10">
                <div className="text-5xl md:text-6xl font-semibold text-[#0B1F3A]/10 tracking-tight">
                  {step.idx}
                </div>
                <div className="mt-6 text-xl font-semibold text-[#0B1F3A] tracking-tight">
                  {t(step.title)}
                </div>
                <p className="mt-4 text-[#4B5563] text-sm leading-relaxed">{t(step.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B1F3A] text-white" data-testid="about-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="mono text-[11px] tracking-[0.2em] uppercase text-white/60">
              {t(ui.about.ctaEyebrow)}
            </div>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {t(ui.about.ctaTitle)}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Link
              to="/products"
              data-testid="about-products-cta"
              className="inline-flex items-center justify-between gap-8 bg-white text-[#0B1F3A] pl-6 pr-4 py-4 text-sm tracking-wide w-full md:w-auto hover:bg-white/90 transition-colors"
            >
              <span>{t(ui.common.browseProducts)}</span>
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
